import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { useCookies } from 'react-cookie';

type CampaignTrace = Record<string, string[]>;

const STORAGE_KEY = 'WC_CampaignTrace';
const MAX_RECORDS = 10;

export const useTrackUTMSearchParams = () => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const provider = searchParams?.get('utm_source');
	const [cookies, setCookie] = useCookies([STORAGE_KEY]);

	useEffect(() => {
		if (!pathname) return;
		if (!provider) return;

		const rawParams = searchParams?.toString();
		if (!rawParams) return;

		try {
			const existingTrace: CampaignTrace = cookies.WC_CampaignTrace ?? {};
			const existingRecords = existingTrace[provider] ?? [];

			if (existingRecords.includes(rawParams)) {
				// already recorded, nothing to update: avoid calling setCookie so we
				// don't re-trigger this effect (cookies.WC_CampaignTrace gets a new
				// object reference on every setCookie call, which would loop forever)
				return;
			}

			const campaignTrace: CampaignTrace = {
				...existingTrace,
				[provider]: [...existingRecords, rawParams],
			};

			const totalRecords = Object.values(campaignTrace).flat().length;

			if (totalRecords > MAX_RECORDS) {
				const firstProvider = Object.keys(campaignTrace)[0];
				campaignTrace[firstProvider] = campaignTrace[firstProvider].slice(1);

				if (campaignTrace[firstProvider].length === 0) {
					delete campaignTrace[firstProvider];
				}
			}

			setCookie(STORAGE_KEY, campaignTrace, {
				path: '/',
				sameSite: 'lax',
			});
		} catch (error) {
			console.error('[WC_CampaignTrace] error:', error);
		}
	}, [cookies.WC_CampaignTrace, pathname, provider, searchParams, setCookie]);
};
